import React, { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { DashboardShell } from "@/components/DashboardLayout";
import { BackButton } from "@/components/Layout";
import { CheckCircle2, Copy, AlertTriangle, XCircle, Info } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";

const CHECKS = [
  { id: 1, label: "Merchant signup", path: "Sign up via /signup — phone or email" },
  { id: 2, label: "KYC submission", path: "Submit via /dashboard/kyc with ID copy upload" },
  { id: 3, label: "KYC approval", path: "Admin approves at /admin/kyc" },
  { id: 4, label: "Wallet top-up", path: "Merchant requests at /dashboard/wallet; Admin approves at /admin/wallet" },
  { id: 5, label: "Voucher generation", path: "Generate at /dashboard/vouchers — wallet is debited atomically" },
  { id: 6, label: "Partial redemption", path: "Redeem partial amount at /dashboard/redeem multiple times" },
  { id: 7, label: "Balance tracking", path: "Run multiple redemptions, balance must decrement correctly" },
  { id: 8, label: "Receipt generation", path: "After redeem, receipt block shows printable summary" },
  { id: 9, label: "SMS workflow", path: "View /admin/sms — SMSPortal sent / mock entries" },
  { id: 10, label: "Voucher depletion", path: "Redeem until balance = 0; status auto-flips to fully_redeemed" },
  { id: 11, label: "Transaction history", path: "View /dashboard/history and /admin/audit" },
  { id: 12, label: "Expiry handling", path: "Vouchers with past expires_at are auto-rejected on redemption" },
];

export default function QA() {
  const { env } = useAuth();
  const [readiness, setReadiness] = useState(null);
  const [smsStats, setSmsStats] = useState(null);
  const [xlearna, setXlearna] = useState(null);

  useEffect(() => {
    api.get("/admin/production-readiness").then(({ data }) => setReadiness(data)).catch(() => {});
    api.get("/admin/sms-stats").then(({ data }) => setSmsStats(data)).catch(() => {});
    api.get("/admin/xlearna/status").then(({ data }) => setXlearna(data)).catch(() => {});
  }, []);

  // If dev OTP is disabled (i.e. production), this console must not be accessible.
  if (env && env.enable_dev_otp === false) return <Navigate to="/admin" replace />;

  const copy = (txt) => { navigator.clipboard.writeText(txt); toast.success("Copied"); };
  const envColor =
    env?.environment === "production"
      ? "bg-red-50 text-red-700 border-red-200"
      : env?.environment === "staging"
      ? "bg-orange-50 text-orange-700 border-orange-200"
      : "bg-[#00C4B4]/10 text-[#00C4B4] border-[#00C4B4]/30";

  return (
    <DashboardShell role="admin">
      <BackButton to="/admin" />
      <p className="text-xs tracking-[0.22em] uppercase font-bold text-[#00C4B4]">QA console</p>
      <div className="flex items-center justify-between flex-wrap gap-3 mt-2">
        <h1 className="font-display text-3xl font-bold text-[#0A2540]">FlexiVouch staging QA</h1>
        <span data-testid="qa-env-badge" className={`text-xs uppercase tracking-[0.18em] px-3 py-1.5 rounded-full border font-bold ${envColor}`}>
          {env?.environment || "unknown"}
        </span>
      </div>

      {/* SEEDED CREDENTIALS — only rendered in dev/staging */}
      <div className="mt-6 bg-[#0A2540] text-white rounded-2xl p-6">
        <p className="text-xs uppercase tracking-[0.18em] text-[#00C4B4] font-bold">Seeded credentials</p>
        <div className="mt-4 grid sm:grid-cols-2 gap-4">
          <CredCard label="Admin" email="admin@flexivouch.co.za" password="FlexiAdmin@2026" copy={copy} />
          <CredCard label="Test merchant (KYC approved, R10,000 float)" email="merchant@flexivouch.co.za" password="MerchantTest@2026" copy={copy} />
        </div>
        <div className="mt-5 bg-white/8 border border-white/10 rounded-lg p-4">
          <p className="text-xs uppercase tracking-[0.18em] text-[#00C4B4] font-bold">Seeded voucher PIN</p>
          <div className="flex items-center justify-between mt-2">
            <code className="font-mono text-lg">1234-5678-9012-3456</code>
            <button data-testid="qa-copy-pin" onClick={() => copy("1234-5678-9012-3456")} className="text-xs text-[#00C4B4] inline-flex items-center gap-1.5 hover:text-white"><Copy size={12} />Copy</button>
          </div>
          <p className="text-xs text-white/70 mt-2">Value R200.00 · active · expires in 3 years</p>
        </div>
      </div>

      {/* PRODUCTION READINESS */}
      <div className="mt-6 bg-white border border-[#E5E7EB] rounded-2xl p-6" data-testid="prod-readiness-card">
        <p className="text-xs tracking-[0.22em] uppercase font-bold text-[#00C4B4]">Production readiness</p>
        <h3 className="font-display mt-2 text-xl font-semibold text-[#0A2540]">
          {readiness?.all_ok ? "All critical gates pass" : "Action required before go-live"}
        </h3>
        <div className="mt-4 space-y-2">
          {readiness?.checks?.map((c) => {
            const Icon = c.severity === "info" ? Info : c.ok ? CheckCircle2 : c.severity === "warning" ? AlertTriangle : XCircle;
            const color =
              c.severity === "info"
                ? "text-[#0A2540]"
                : c.ok
                ? "text-[#00C853]"
                : c.severity === "warning"
                ? "text-orange-600"
                : "text-red-600";
            return (
              <div key={c.name} className="flex items-start gap-3 text-sm">
                <Icon size={18} className={`${color} mt-0.5 shrink-0`} />
                <div>
                  <p className="font-semibold text-[#0A2540]">{c.name}</p>
                  <p className="text-xs text-[#666666]">{c.detail}</p>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* SMS HEALTH */}
      <div className="mt-6 grid grid-cols-1 lg:grid-cols-2 gap-5">
        <div className="bg-white border border-[#E5E7EB] rounded-2xl p-6" data-testid="sms-health-card">
          <p className="text-xs tracking-[0.22em] uppercase font-bold text-[#00C4B4]">SMS health (SMSPortal)</p>
          <div className="mt-3 flex items-center justify-between">
            <span className="text-sm text-[#666666]">Provider configured</span>
            <span className={`text-xs font-bold uppercase ${smsStats?.provider_configured ? "text-[#00C853]" : "text-orange-600"}`}>
              {smsStats?.provider_configured ? "yes" : "no — sends are MOCKED"}
            </span>
          </div>
          <div className="mt-4 space-y-1.5 text-sm">
            {(smsStats?.summary || []).map((s, i) => (
              <div key={i} className="flex justify-between text-xs">
                <span className="text-[#666666]">{s.type || "—"} · {s.status}</span>
                <span className="font-mono text-[#0A2540]">{s.count}</span>
              </div>
            ))}
            {(!smsStats?.summary || smsStats.summary.length === 0) && (
              <p className="text-xs text-[#999999]">No SMS sent yet.</p>
            )}
          </div>
          {smsStats?.recent_failures?.length > 0 && (
            <details className="mt-3 bg-red-50 border border-red-200 rounded-lg p-3">
              <summary className="text-xs font-semibold text-red-700 cursor-pointer">{smsStats.recent_failures.length} recent failure(s)</summary>
              <ul className="mt-2 space-y-1 text-[11px] text-red-900">
                {smsStats.recent_failures.slice(0, 5).map((f) => (
                  <li key={f.id}><strong>{f.to}</strong> · {f.error}</li>
                ))}
              </ul>
            </details>
          )}
        </div>

        <div className="bg-white border border-[#E5E7EB] rounded-2xl p-6" data-testid="xlearna-status-card">
          <p className="text-xs tracking-[0.22em] uppercase font-bold text-[#00C4B4]">XLearna integration</p>
          <div className="mt-3 flex items-center justify-between">
            <span className="text-sm text-[#666666]">Configured</span>
            <span className={`text-xs font-bold uppercase ${xlearna?.configured ? "text-[#00C853]" : "text-orange-600"}`}>
              {xlearna?.configured ? "yes" : "no — standalone mode"}
            </span>
          </div>
          <p className="mt-3 text-xs text-[#666666] leading-relaxed">{xlearna?.notes}</p>
          {xlearna?.planned_contract?.length > 0 && (
            <ul className="mt-3 space-y-1 text-[11px]">
              {xlearna.planned_contract.map((c, i) => (
                <li key={i} className="flex gap-2"><code className="font-mono text-[#00C4B4]">{c.method}</code><code className="font-mono text-[#0A2540]">{c.path}</code></li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {/* CHECKLIST */}
      <div className="mt-6 bg-white border border-[#E5E7EB] rounded-2xl divide-y divide-[#E5E7EB]">
        {CHECKS.map((c) => (
          <div key={c.id} className="p-5 flex items-start gap-4" data-testid={`qa-check-${c.id}`}>
            <CheckCircle2 size={18} className="text-[#00C4B4] mt-0.5 shrink-0" />
            <div>
              <p className="font-display font-semibold text-[#0A2540]">{c.id}. {c.label}</p>
              <p className="text-sm text-[#666666] mt-0.5">{c.path}</p>
            </div>
          </div>
        ))}
      </div>
    </DashboardShell>
  );
}

function CredCard({ label, email, password, copy }) {
  return (
    <div className="bg-white/8 border border-white/10 rounded-lg p-4">
      <p className="text-xs uppercase tracking-[0.18em] text-[#00C4B4] font-bold">{label}</p>
      <div className="flex items-center justify-between mt-2"><code className="font-mono text-sm">{email}</code><button onClick={() => copy(email)} className="text-xs text-[#00C4B4] hover:text-white"><Copy size={12} /></button></div>
      <div className="flex items-center justify-between mt-1"><code className="font-mono text-sm">{password}</code><button onClick={() => copy(password)} className="text-xs text-[#00C4B4] hover:text-white"><Copy size={12} /></button></div>
    </div>
  );
}
